import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
// #2542 — use getFleetHttpUrl() (lazy) instead of stale module-level constant.
import { getFleetHttpUrl, type SessionMeta } from "../lib/fleetRoles";
import { fleetApi } from "../api/fleetApi";
import { useAuth } from "./AuthContext";

export interface SessionMetaContextValue {
  sessionMeta: Record<string, SessionMeta>;
  loading:     boolean;
  refresh:     () => void;
}

const SessionMetaContext = createContext<SessionMetaContextValue>({
  sessionMeta: {},
  loading:     false,
  refresh:     () => {},
});

export function SessionMetaProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  const [sessionMeta, setSessionMeta] = useState<Record<string, SessionMeta>>({});
  const [loading, setLoading] = useState(false);

  const fetchMeta = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      // Fetch the tenant's project list. Empty = no projects yet (provisioning).
      let projects: string[] = [];
      try {
        projects = await fleetApi.getProjectNames();
      } catch { /* network error — leave projects empty */ }

      if (projects.length === 0) {
        setSessionMeta({});
        return;
      }

      const results = await Promise.all(
        projects.map((p) =>
          fetch(`${getFleetHttpUrl()}/fleet/sessions/metadata?project=${encodeURIComponent(p)}`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then((r) => r.ok ? r.json() as Promise<{ sessions?: Record<string, SessionMeta> }> : null)
            .catch(() => null)
        )
      );

      const merged: Record<string, SessionMeta> = {};
      for (const body of results) {
        if (body?.sessions) {
          for (const [key, apiMeta] of Object.entries(body.sessions)) {
            merged[key] = apiMeta;
          }
        }
      }
      setSessionMeta(merged);
    } catch {
      // network error — keep whatever was loaded
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);

  return (
    <SessionMetaContext.Provider value={{ sessionMeta, loading, refresh: fetchMeta }}>
      {children}
    </SessionMetaContext.Provider>
  );
}

export function useSessionMeta(): SessionMetaContextValue {
  return useContext(SessionMetaContext);
}
